import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { getPanelsByTabId } from "../modules/CustomDashboardModule/services/DashboardConfigurationService";
import DashboardWelcomePage from "../modules/CustomDashboardModule/components/welcome/DashBoardWelcomePage";
import { GridStack } from "../modules/CustomDashboardModule/components/gridStack/GridStack";
import { useReduxQuery } from "./useReduxQuery";

const TAB_CONTENT_MAP = {
  welcome: DashboardWelcomePage,
  gridstack: GridStack,
};

/**
 * Unifies logic for tabs within a dashboard
 * 1. Listing Tabs
 * 2. Fetching Tabs
 * And whatnot
 * @param {Object} props - The module's config so this hook knows what to update
 * @param {string} props.tabId - the tab id to fetch the tab data on
 * @param {function} props.selector - a redux selector so we can return the tab data from this hook instead of having multiple selectors all over the place
 * @param {function} props.onResponse - a redux action to pass in the fetched tab data
 * @param {function} props.onLoading - a redux action to update that the tab data are being fetched
 * @param {function} props.onError - a redux action to set a tab data fetch error
 * @returns {Object}
 */
export const useTabContentQuery = ({
  stream,
  selectors,
  actions,
  onResponse,
  onLoading,
  onError,
}) => {
  const tabId = useSelector(selectors.tabId);
  const content = useSelector(selectors.content);

  const query = useReduxQuery({
    queryFn: getPanelsByTabId,
    args: [tabId],
    selector: selectors.isLoading,
    onLoading,
    onResponse,
    onError,
  });

  useEffect(() => {
    query();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId]);

  const Component = useMemo(
    () => (content ? TAB_CONTENT_MAP[content.content] : React.Fragment),
    [content?.content]
  );

  const props = content
    ? { ...content.props, actions, stream, tabId }
    : undefined;

  return { TabContent: Component, props };
};
